import React from "react";
import {
  Users,
  FileText,
  Lock,
  ShieldAlert,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Mail,
} from "lucide-react";
import PrivacyImage from "../images/privacy-policy.png";

function PrivacyPolicy() {
  return (
    <div className="p-4 md:p-8 mx-auto mt-6 md:mt-12 shadow-xl max-w-4xl bg-blue-50 rounded-2xl">
      {/* Header */}
      <div className="text-center">
        <h1 className="mb-4 text-3xl sm:text-4xl font-bold text-blue-700">
          Privacy Policy
        </h1>
        <p className="text-gray-700 text-sm sm:text-base">
          Welcome to <span className="font-bold text-blue-700">EDIT EZY</span>.
          Your privacy is important to us. This policy explains how we collect,
          use, and protect your data.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-6 mt-8">
        <Section
          icon={Users}
          title="Information We Collect"
          iconColor="text-blue-500"
          borderColor="border-blue-500"
          content={
            <ul className="pl-4 text-gray-700 text-sm space-y-2 list-disc">
              <li><strong>Personal Data:</strong></li>
              <ul className="pl-6 list-disc">
                <li>Name, Phone, Email, Date of Birth</li>
                <li>Profile image (optional)</li>
                <li>Uploaded images, designs, or content</li>
                <li>Messages & inquiries</li>
              </ul>
              <li><strong>Automatic Data:</strong></li>
              <ul className="pl-6 list-disc">
                <li>Device info (OS, model, ID)</li>
                <li>Usage data (pages visited, features used)</li>
              </ul>
            </ul>
          }
        />

        <Section
          icon={FileText}
          title="How We Use Your Info"
          iconColor="text-green-500"
          borderColor="border-green-500"
          content={
            <ul className="pl-4 text-gray-700 text-sm space-y-2 list-disc">
              <li>Access app features and tools</li>
              <li>Customize and save poster designs</li>
              <li>Improve user experience</li>
              <li>Respond to support and feedback</li>
            </ul>
          }
        />

        <Section
          icon={Lock}
          title="Data Protection"
          iconColor="text-red-500"
          borderColor="border-red-500"
          content={
            <ul className="pl-4 text-gray-700 text-sm space-y-2 list-disc">
              <li>Encryption of sensitive data</li>
              <li>Secure cloud storage</li>
              <li>Restricted access to personal info</li>
            </ul>
          }
        />

        <Section
          icon={ShieldAlert}
          title="Sharing Your Data"
          iconColor="text-purple-500"
          borderColor="border-purple-500"
          content={
            <ul className="pl-4 text-gray-700 text-sm space-y-2 list-disc">
              <li>We don’t sell your data. We may share with:</li>
              <ul className="pl-6 list-disc">
                <li>Cloud storage providers</li>
                <li>Analytics & crash reporting tools</li>
                <li>Legal authorities (if required)</li>
              </ul>
            </ul>
          }
        />

        <Section
          icon={Trash2}
          title="Your Rights"
          iconColor="text-yellow-500"
          borderColor="border-yellow-500"
          content={
            <ul className="pl-4 text-gray-700 text-sm space-y-2 list-disc">
              <li>Access and review your data</li>
              <li>Update your profile image</li>
              <li>Delete your account anytime</li>
              <li>
                Request via:{" "}
                <span className="text-blue-700 font-semibold">
                  contact.editezy@gmail.com
                </span>
              </li>
            </ul>
          }
        />

        <Section
          icon={RefreshCw}
          title="Data Retention"
          iconColor="text-indigo-500"
          borderColor="border-indigo-500"
          content={
            <p className="text-sm text-gray-700">
              We keep your data only as long as needed to provide services or
              comply with the law.
            </p>
          }
        />

        <Section
          icon={AlertTriangle}
          title="Children's Privacy"
          iconColor="text-pink-500"
          borderColor="border-pink-500"
          content={
            <p className="text-sm text-gray-700">
              EDIT EZY is not intended for children under 13. We do not
              knowingly collect data from them.
            </p>
          }
        />

        <Section
          icon={RefreshCw}
          title="Policy Updates"
          iconColor="text-teal-500"
          borderColor="border-teal-500"
          content={
            <p className="text-sm text-gray-700">
              We may update this policy. Changes will be shared in-app or via
              email. Continued use means acceptance.
            </p>
          }
        />

        <Section
          icon={Mail}
          title="Contact Us"
          iconColor="text-gray-500"
          borderColor="border-gray-500"
          content={
            <p className="text-sm text-gray-700">
              Questions? Reach us at:{" "}
              <span className="text-blue-700 font-semibold">
                contact.editezy@gmail.com
              </span>
            </p>
          }
        />
      </div>

      {/* Image */}
      <div className="flex justify-center mt-8">
        <img
          src={PrivacyImage}
          alt="Privacy Policy"
          className="w-24 sm:w-32 md:w-40 h-auto rounded-lg shadow-md bg-transparent hidden sm:block"
        />
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, iconColor, borderColor, content }) => (
  <section
    className={`p-4 sm:p-6 bg-white border-l-4 shadow-md rounded-xl ${borderColor}`}
  >
    <h2 className="flex items-center justify-start text-lg sm:text-xl font-semibold text-gray-800 mb-3">
      <Icon className={`mr-3 ${iconColor} w-5 h-5`} /> {title}
    </h2>
    {content}
  </section>
);

export default PrivacyPolicy;
